import { useState } from "react";
import { toast } from "sonner";
import { Clock, Mail, MapPin, Phone } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { Field, FormSuccess, inputCls, SubmitButton } from "@/components/forms";
import { sendContactMessage } from "@/lib/api";

const infoItems = [
  { icon: MapPin, label: "Visit us", value: "Johannesburg, Gauteng, South Africa" },
  { icon: Mail, label: "Email us", value: "hello@dynamicappsolutions.co.za" },
  { icon: Phone, label: "Call Donavan", value: "+27 64 891 0028" },
  { icon: Clock, label: "Hours", value: "Mon – Fri, 08:00 – 17:00 SAST" },
];

const empty = { name: "", email: "", phone: "", subject: "General Enquiry", message: "" };

export default function Contact() {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await sendContactMessage(form);
      setSent(true);
      toast.success("Message received — we'll be in touch soon.");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <PageHero
        overline="Contact Us"
        lines={["Talk to a human,", "not a helpdesk"]}
        copy="Questions about POS devices, payment gateways or value-added services? Send a message and our Johannesburg team will get back to you."
        testid="contact-hero"
      />

      <section className="mx-auto max-w-7xl px-6 pb-28 md:px-12">
        <div className="grid gap-10 lg:grid-cols-12">
          <div className="space-y-6 lg:col-span-5">
            {infoItems.map((item, i) => (
              <Reveal key={item.label} delay={i * 0.08}>
                <div
                  data-testid={`contact-info-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="flex items-center gap-5 rounded-3xl border border-neutral-200 bg-white p-6 transition-all duration-300 hover:-translate-y-1 hover:border-das-violet/40"
                >
                  <span className="grid h-13 w-13 shrink-0 place-items-center rounded-2xl bg-das-violet/10 p-3.5 text-das-deep">
                    <item.icon className="h-6 w-6" />
                  </span>
                  <div>
                    <p className="text-xs font-bold uppercase tracking-[0.18em] text-neutral-500">
                      {item.label}
                    </p>
                    <p className="mt-1 font-display text-base font-semibold text-das-ink">
                      {item.value}
                    </p>
                  </div>
                </div>
              </Reveal>
            ))}
            <Reveal delay={0.35}>
              <div className="rounded-3xl bg-das-ink p-8 text-white">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-das-purple">
                  Service areas
                </p>
                <div className="mt-4 flex flex-wrap gap-2">
                  {["Johannesburg", "Gauteng", "South Africa", "Botswana"].map((m) => (
                    <span
                      key={m}
                      className="rounded-full border border-white/15 px-4 py-1.5 text-xs font-semibold text-white/80"
                    >
                      {m}
                    </span>
                  ))}
                </div>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.15} className="lg:col-span-7">
            <div className="rounded-[2rem] border border-neutral-200 bg-white p-8 shadow-ambient sm:p-12">
              {sent ? (
                <FormSuccess
                  title="Message sent!"
                  message="Thank you for reaching out to Dynamic App Solutions. A member of our team will get back to you within one business day."
                  onReset={() => {
                    setSent(false);
                    setForm(empty);
                  }}
                  testid="contact-success-message"
                />
              ) : (
                <form onSubmit={onSubmit} className="space-y-6" data-testid="contact-form">
                  <div className="grid gap-6 sm:grid-cols-2">
                    <Field label="Your Name" htmlFor="contact-name">
                      <input
                        id="contact-name"
                        data-testid="contact-name-input"
                        required
                        value={form.name}
                        onChange={set("name")}
                        placeholder="Thandi Mokoena"
                        className={inputCls}
                      />
                    </Field>
                    <Field label="Email Address" htmlFor="contact-email">
                      <input
                        id="contact-email"
                        type="email"
                        data-testid="contact-email-input"
                        required
                        value={form.email}
                        onChange={set("email")}
                        placeholder="you@business.co.za"
                        className={inputCls}
                      />
                    </Field>
                  </div>
                  <div className="grid gap-6 sm:grid-cols-2">
                    <Field label="Phone (optional)" htmlFor="contact-phone">
                      <input
                        id="contact-phone"
                        data-testid="contact-phone-input"
                        value={form.phone}
                        onChange={set("phone")}
                        placeholder="+27 82 000 0000"
                        className={inputCls}
                      />
                    </Field>
                    <Field label="Topic" htmlFor="contact-subject">
                      <select
                        id="contact-subject"
                        data-testid="contact-subject-select"
                        value={form.subject}
                        onChange={set("subject")}
                        className={inputCls}
                      >
                        {["General Enquiry", "POS Hardware", "Payment Gateway", "Value-Added Services", "Support", "Partnership"].map((s) => (
                          <option key={s}>{s}</option>
                        ))}
                      </select>
                    </Field>
                  </div>
                  <Field label="Your Message" htmlFor="contact-message">
                    <textarea
                      id="contact-message"
                      data-testid="contact-message-input"
                      required
                      rows={5}
                      value={form.message}
                      onChange={set("message")}
                      placeholder="Tell us about your business and what you need…"
                      className={`${inputCls} resize-none`}
                    />
                  </Field>
                  <SubmitButton loading={loading} testid="contact-submit-button">
                    Send Message
                  </SubmitButton>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
